import { Component } from '@theme/component';
import { normalizeString } from '@theme/utilities';

const DEFAULT_PAGE_SIZE = 12;

/**
 * @typedef {Object} Refs
 * @property {HTMLInputElement} [search]
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} [count]
 * @property {HTMLButtonElement[]} [filterButtons]
 * @property {HTMLElement} [gridContainer]
 * @property {HTMLElement} [resultsBar]
 * @property {HTMLElement} [resultsCount]
 * @property {HTMLButtonElement} [clearAllBtn]
 * @property {HTMLElement} [azSidebar]
 * @property {HTMLButtonElement[]} [azLetters]
 * @property {HTMLElement} [pagination]
 * @property {HTMLButtonElement} [showMoreBtn]
 * @property {HTMLButtonElement} [showAllBtn]
 * @property {HTMLButtonElement} [collapseBtn]
 *
 * @extends {Component<Refs>}
 */
class IngredientFilterComponent extends Component {
  #abortController = new AbortController();
  #search = '';
  #concern = 'all';
  #category = 'all';
  #pageSize = DEFAULT_PAGE_SIZE;
  #visibleLimit = DEFAULT_PAGE_SIZE;

  /** @type {HTMLElement[]} */
  #sortedSlides = [];

  /** @type {HTMLElement[]} */
  #matchedSlides = [];

  #isAlphabetical = false;

  /** @type {Map<string, HTMLElement>} */
  #letterHeaders = new Map();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.#isAlphabetical = !!this.refs.gridContainer;

    // Read configurable page size from grid block's data attribute
    const gridWrapper = this.refs.gridContainer?.closest('[data-initial-items]');
    if (gridWrapper) {
      const parsed = parseInt(gridWrapper.dataset.initialItems, 10);
      if (parsed > 0) {
        this.#pageSize = parsed;
        this.#visibleLimit = parsed;
      }
    }

    this.refs.search?.addEventListener('input', this.#onSearchInput, { signal });

    for (const btn of this.refs.filterButtons ?? []) {
      btn.addEventListener('click', this.#onFilterClick, { signal });
    }

    if (this.#isAlphabetical) {
      this.#initAlphabetical(signal);
    } else {
      this.#filter();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#isAlphabetical = !!this.refs.gridContainer;
    if (this.#isAlphabetical) {
      this.#sortedSlides = [...(this.refs.slides ?? [])];
      this.#sortedSlides.sort((a, b) =>
        (a.dataset.libName || '').localeCompare(b.dataset.libName || '')
      );
    }
    this.#filter();
  }

  /**
   * Sets up alphabetical grid mode: sorts cards, inserts letter headers,
   * binds pagination and A-Z sidebar events.
   * @param {AbortSignal} signal
   */
  #initAlphabetical(signal) {
    const slides = this.refs.slides ?? [];
    this.#sortedSlides = [...slides].sort((a, b) =>
      (a.dataset.libName || '').localeCompare(b.dataset.libName || '')
    );

    const container = this.refs.gridContainer;
    if (container) {
      let currentLetter = '';
      for (const slide of this.#sortedSlides) {
        const letter = slide.dataset.libLetter || '';
        if (letter && letter !== currentLetter) {
          currentLetter = letter;
          const header = document.createElement('div');
          header.className = 'ing-grid__letter-header';
          header.dataset.letter = letter;
          header.setAttribute('aria-hidden', 'true');
          header.innerHTML =
            `<span class="ing-grid__letter-text">${letter.toUpperCase()}</span>` +
            `<span class="ing-grid__letter-count"></span>`;
          header.hidden = true;
          container.appendChild(header);
          this.#letterHeaders.set(letter, header);
        }
        container.appendChild(slide);
      }
    }

    this.refs.showMoreBtn?.addEventListener('click', this.#onShowMore, { signal });
    this.refs.showAllBtn?.addEventListener('click', this.#onShowAll, { signal });
    this.refs.collapseBtn?.addEventListener('click', this.#onCollapse, { signal });
    this.refs.clearAllBtn?.addEventListener('click', this.#onClearAll, { signal });

    for (const btn of this.refs.azLetters ?? []) {
      btn.addEventListener('click', this.#onAzLetterClick, { signal });
    }

    this.#filter();
  }

  // === Event handlers ===

  #onSearchInput = () => {
    this.#search = normalizeString(this.refs.search?.value || '');
    this.#visibleLimit = this.#pageSize;
    this.#filter();
  };

  /** @param {Event} event */
  #onFilterClick = (event) => {
    const btn = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const filterType = btn.dataset.filterType;
    const filterValue = btn.dataset.filterValue || 'all';

    for (const b of this.refs.filterButtons ?? []) {
      if (b.dataset.filterType === filterType) {
        b.classList.toggle('is-active', b === btn);
      }
    }

    if (filterType === 'concern') {
      this.#concern = filterValue;
    } else if (filterType === 'category') {
      this.#category = filterValue;
    }

    this.#visibleLimit = this.#pageSize;
    this.#filter();
  };

  #onShowMore = () => {
    this.#visibleLimit += this.#pageSize;
    this.#applyPagination();
  };

  #onShowAll = () => {
    this.#visibleLimit = this.#matchedSlides.length;
    this.#applyPagination();
  };

  #onCollapse = () => {
    this.#visibleLimit = this.#pageSize;
    this.#applyPagination();
    this.refs.gridContainer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /** @param {Event} event */
  #onAzLetterClick = (event) => {
    const btn = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const letter = btn.dataset.letter;
    if (!letter || btn.classList.contains('is-inactive')) return;

    this.#visibleLimit = this.#matchedSlides.length;
    this.#applyPagination();

    const header = this.#letterHeaders.get(letter);
    if (header) {
      header.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  #onClearAll = () => {
    this.#search = '';
    this.#concern = 'all';
    this.#category = 'all';
    this.#visibleLimit = this.#pageSize;

    if (this.refs.search) {
      this.refs.search.value = '';
    }

    for (const btn of this.refs.filterButtons ?? []) {
      const isAll = btn.dataset.filterValue === 'all';
      btn.classList.toggle('is-active', isAll);
    }

    this.#filter();
  };

  // === Filtering ===

  get #hasFilterControls() {
    return !!(this.refs.search || this.refs.filterButtons?.length);
  }

  get #isFiltered() {
    return this.#search !== '' || this.#concern !== 'all' || this.#category !== 'all';
  }

  #filter() {
    if (this.#isAlphabetical) {
      this.#filterAlphabetical();
    } else {
      this.#filterSimple();
    }
  }

  /** Simple filter for carousel/child-block mode (original behavior) */
  #filterSimple() {
    let visible = 0;

    for (const card of this.refs.slides ?? []) {
      const name = card.dataset.libName || '';
      const cat = card.dataset.libCategory || '';
      const con = card.dataset.libConcern || '';
      const match =
        (!this.#search || name.includes(this.#search)) &&
        (this.#concern === 'all' || con === this.#concern) &&
        (this.#category === 'all' || cat === this.#category);

      card.hidden = !match;
      if (match) visible++;
    }

    if (this.refs.empty) {
      this.refs.empty.hidden = visible > 0 || !this.#hasFilterControls || !this.#isFiltered;
    }

    if (this.refs.count) {
      this.refs.count.textContent = `${visible} ${this.dataset.countSuffix || ''}`;
    }
  }

  /** Alphabetical filter with pagination, letter headers, and A-Z sidebar */
  #filterAlphabetical() {
    this.#matchedSlides = [];

    for (const card of this.#sortedSlides) {
      const name = card.dataset.libName || '';
      const cat = card.dataset.libCategory || '';
      const con = card.dataset.libConcern || '';
      const match =
        (!this.#search || name.includes(this.#search)) &&
        (this.#concern === 'all' || con === this.#concern) &&
        (this.#category === 'all' || cat === this.#category);

      if (match) {
        this.#matchedSlides.push(card);
      }
    }

    this.#applyPagination();
  }

  /** Shows/hides cards based on pagination state and updates all UI */
  #applyPagination() {
    const isFiltered = this.#isFiltered;
    const matched = this.#matchedSlides;
    const total = matched.length;
    const limit = isFiltered ? total : Math.min(this.#visibleLimit, total);

    for (const slide of this.#sortedSlides) {
      slide.hidden = true;
    }

    for (let i = 0; i < matched.length; i++) {
      matched[i].hidden = i >= limit;
    }

    this.#updateLetterHeaders();
    this.#updateAzSidebar(isFiltered);
    this.#updatePagination(total, limit, isFiltered);
    this.#updateResultsBar(total, isFiltered);

    if (this.refs.count) {
      this.refs.count.textContent = `${total} ${this.dataset.countSuffix || ''}`;
    }

    if (this.refs.empty) {
      this.refs.empty.hidden = total > 0 || !this.#hasFilterControls || !this.#isFiltered;
    }
  }

  /** Shows/hides letter headers and updates their counts */
  #updateLetterHeaders() {
    /** @type {Map<string, number>} */
    const letterCounts = new Map();

    for (const slide of this.#sortedSlides) {
      if (!slide.hidden) {
        const letter = slide.dataset.libLetter || '';
        letterCounts.set(letter, (letterCounts.get(letter) || 0) + 1);
      }
    }

    let firstVisible = true;
    for (const [letter, header] of this.#letterHeaders) {
      const count = letterCounts.get(letter) || 0;
      header.hidden = count === 0;
      header.classList.toggle('is-first-visible', count > 0 && firstVisible);
      if (count > 0) firstVisible = false;
      const countEl = header.querySelector('.ing-grid__letter-count');
      if (countEl) {
        countEl.textContent = count > 0 ? `(${count})` : '';
      }
    }
  }

  /**
   * Updates A-Z sidebar letter states based on matched results.
   * Hides the entire sidebar when filters are active.
   * @param {boolean} isFiltered
   */
  #updateAzSidebar(isFiltered) {
    if (!this.refs.azSidebar) return;

    this.refs.azSidebar.hidden = isFiltered;

    if (isFiltered) return;

    const activeLetters = new Set();
    for (const slide of this.#matchedSlides) {
      const letter = slide.dataset.libLetter || '';
      if (letter) activeLetters.add(letter);
    }

    for (const btn of this.refs.azLetters ?? []) {
      const letter = btn.dataset.letter || '';
      const hasCards = activeLetters.has(letter);
      btn.classList.toggle('is-active', hasCards);
      btn.classList.toggle('is-inactive', !hasCards);
      btn.disabled = !hasCards;
    }
  }

  /**
   * Updates pagination button text and visibility.
   * @param {number} total
   * @param {number} shown
   * @param {boolean} isFiltered
   */
  #updatePagination(total, shown, isFiltered) {
    if (!this.refs.pagination) return;

    const remaining = total - shown;
    const allShown = remaining <= 0;

    this.refs.pagination.hidden = isFiltered || total === 0;

    if (isFiltered) return;

    if (this.refs.showMoreBtn) {
      this.refs.showMoreBtn.hidden = allShown;
      if (!allShown) {
        const tpl = this.dataset.tShowMore || 'Show More ([remaining] of [total] remaining)';
        this.refs.showMoreBtn.textContent = tpl
          .replace('[remaining]', String(remaining))
          .replace('[total]', String(total));
      }
    }

    if (this.refs.showAllBtn) {
      this.refs.showAllBtn.hidden = allShown;
      if (!allShown) {
        const tpl = this.dataset.tShowAll || 'Show all [total] ingredients';
        this.refs.showAllBtn.textContent = tpl.replace('[total]', String(total));
      }
    }

    if (this.refs.collapseBtn) {
      this.refs.collapseBtn.hidden = !allShown || total <= this.#pageSize;
      if (!this.refs.collapseBtn.hidden) {
        this.refs.collapseBtn.textContent = this.dataset.tCollapse || 'Collapse list';
      }
    }
  }

  /**
   * Shows/hides the results summary bar when filters are active.
   * @param {number} total
   * @param {boolean} isFiltered
   */
  #updateResultsBar(total, isFiltered) {
    if (!this.refs.resultsBar) return;

    this.refs.resultsBar.hidden = !isFiltered;

    if (isFiltered && this.refs.resultsCount) {
      const tpl = total === 1
        ? (this.dataset.tShowingOne || 'Showing [count] result')
        : (this.dataset.tShowingOther || 'Showing [count] results');
      this.refs.resultsCount.textContent = tpl.replace('[count]', String(total));
    }
  }
}

customElements.define('ingredient-filter', IngredientFilterComponent);
